import app from 'flarum/forum/app';
/**
 * Page Builder pages (a home page hero, a landing page) are the forum's own
 * words, laid out as HTML: their text is read off the page, sent with the
 * page's id, and swapped in place. The server only translates strings that
 * are in the page's saved layout.
 */

const SKIP = new Set(['SCRIPT', 'STYLE', 'CODE', 'PRE', 'TEXTAREA', 'NOSCRIPT']);

/** Each text node's text as written, so a second pass never re-sends a translation. */
const originals = new WeakMap();

/** page id → original text → translation (or null: asked, nothing to show). */
const done = new Map();
const asking = new Set();
const clean = s => s.replace(/\s+/g, ' ').trim();
function textNodes(root) {
  const nodes = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      let el = node.parentElement;
      while (el && el !== root) {
        if (SKIP.has(el.tagName) || el.isContentEditable) return NodeFilter.FILTER_REJECT;
        el = el.parentElement;
      }
      return /\p{L}{2}/u.test(node.nodeValue || '') ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
    }
  });
  while (walker.nextNode()) nodes.push(walker.currentNode);
  return nodes;
}
function apply(root, translations, rosetta) {
  let any = false;
  for (const node of textNodes(root)) {
    const original = originals.get(node) ?? clean(node.nodeValue || '');
    const translation = translations.get(original);
    if (!translation) continue;
    originals.set(node, original);
    const value = node.nodeValue || '';
    // Keep the spacing around the words: it's what separates them from their neighbours.
    const lead = value.match(/^\s*/)?.[0] || '';
    const trail = value.match(/\s*$/)?.[0] || '';
    if (value !== lead + translation + trail) node.nodeValue = lead + translation + trail;
    any = true;
  }
  if (any) {
    root.setAttribute('lang', rosetta.target);
    root.setAttribute('dir', rosetta.dir);
  }
}
export function translatePages(rosetta) {
  for (const root of Array.from(document.querySelectorAll('[data-rosetta-page]'))) {
    const page = root.getAttribute('data-rosetta-page') || '';
    if (!page) continue;
    const known = done.get(page) || new Map();
    done.set(page, known);
    const missing = new Set();
    for (const node of textNodes(root)) {
      const text = originals.get(node) ?? clean(node.nodeValue || '');
      if (!known.has(text)) missing.add(text);
    }
    apply(root, known, rosetta);
    if (!missing.size || asking.has(page)) continue;
    asking.add(page);
    app.request({
      method: 'POST',
      url: app.forum.attribute('apiUrl') + '/rosetta/page',
      body: {
        locale: rosetta.target,
        page: Number(page),
        texts: [...missing].slice(0, 300)
      },
      errorHandler: () => {}
    }).then(response => {
      for (const text of missing) known.set(text, response.texts?.[text] ?? null);
    }).catch(() => {
      for (const text of missing) known.set(text, null);
    }).finally(() => {
      asking.delete(page);
      const current = document.querySelector(`[data-rosetta-page="${CSS.escape(page)}"]`);
      if (current) apply(current, known, rosetta);
    });
  }
}

/**
 * Pages render their HTML after the route's data arrives, and again when
 * Mithril redraws: watch for that, a beat after it settles.
 */
export function watchPages(rosetta) {
  let timer = 0;
  const run = () => {
    timer = 0;
    translatePages(rosetta);
  };
  new MutationObserver(() => {
    if (!timer && document.querySelector('[data-rosetta-page]')) timer = window.setTimeout(run, 150);
  }).observe(document.body, {
    childList: true,
    subtree: true
  });
}